import React, { useState} from "react"
import { TableHead, TableBody, TableRow, Box, Stack, Typography } from "@mui/material"
import ExpandLess from "@mui/icons-material/ExpandLess"
import ExpandMore from "@mui/icons-material/ExpandMore"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import ConformityTooltip from "./ConformityTooltip"
import PEBlockHead from "./PEBlockHead"
import ExecutionsStatus from "../../../containers/ProductionStepExecutions/ExecutionsStatus"
import IconCheck from "../../../components/Supplier/IconCheck"
import IconClose from "../../../components/Supplier/IconClose"
import { StyledTable, StyledHeaderCell, StyledBodyCell, StyledFooterCell } from "../../../components/styled/StyledTableComponents"
import { COLORS } from "../../../utils"
import { formatEmailToName } from "../../../utils"
import { TESTGAZ_STATUS, groupTestsByThreshold, computeTresholdStatus } from "../../../utils/testGaz"

const PEGazTest = ({ gazTests = [], PEDone = false }) => {
  const [expandedThresholds, setExpandedThresholds] = useState({})

  const groupedTests = groupTestsByThreshold(gazTests)
  // Get and count unique threshold names
  const uniqueNames = [...new Set(gazTests.map(test => test.name))]
  const totalThresholds = uniqueNames.length
  // Count how many of those unique names have at least one SUCCESS
  const totalDone = uniqueNames.filter(name => 
    gazTests.some(test => test.name === name && test.status === "SUCCESS")
  ).length

  return (
    <>
      <PEBlockHead
        title="Tests gaz"
        icon="packaging-paste"
        suffix={`Test ${totalDone}/${totalThresholds}`}
      />

      <Box sx={sx.container}>
        {[...groupedTests.entries()].map(([thresholdName, tests], i) => {
          const status = computeTresholdStatus(tests)
          const isExpanded = expandedThresholds[thresholdName]
          const toggleExpand = () => setExpandedThresholds(prev => ({ ...prev, [thresholdName]: !prev[thresholdName]}))

          return (
            <Box key={i} sx={sx.thresholdBlock}>
              <Stack direction="row" justifyContent="space-between" spacing={1} mb={0.5} mt={0.5} width="100%">
                <Typography sx={sx.thresholdTitle}>{thresholdName}</Typography>

                <Stack direction="row" alignItems="center" spacing={1}>
                  {!PEDone && (
                    <ExecutionsStatus
                      statusValue={status}
                      statusName={status !== TESTGAZ_STATUS.TODO ? "Fait" : "À faire"}
                    />
                  )}

                  {status !== TESTGAZ_STATUS.TODO ? (
                    isExpanded ? 
                      (<ExpandMore onClick={toggleExpand} sx={sx.expandIcon} />) :
                      (<ExpandLess onClick={toggleExpand} sx={sx.expandIcon} />)
                  ) : <Box sx={{ width: "24px" }} />}
                </Stack>
              </Stack>

              {/* Tests for threshold */}
              {isExpanded &&
                tests.map((test, j) =>
                  test.status !== TESTGAZ_STATUS.TODO ? (
                    <Box key={j} sx={sx.testBlock}>
                      <StyledTable>
                        <TableHead>
                          <TableRow>
                            <StyledHeaderCell leftEdge>Emplacement</StyledHeaderCell>
                            <StyledHeaderCell>O2 % (valeur)</StyledHeaderCell>
                            <StyledHeaderCell>CO2 % (valeur)</StyledHeaderCell>
                            <StyledHeaderCell rightEdge>
                              <Stack direction="row" alignItems="center">
                                Conformité
                                <ConformityTooltip>
                                  <HelpOutlineIcon sx={{ ml: 1, cursor: "pointer", fontSize: 24, p: "2px" }} />
                                </ConformityTooltip>
                              </Stack>
                            </StyledHeaderCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {(test.rows || []).map((row, k) => {
                            const isConform = row.conformity.o2 && row.conformity.co2
                            return (
                              <TableRow key={k}>
                                <StyledBodyCell leftEdge>{row.placement}</StyledBodyCell>
                                <StyledBodyCell>{row.o2.toFixed(1)} %</StyledBodyCell>
                                <StyledBodyCell>{row.co2.toFixed(1)} %</StyledBodyCell>
                                <StyledBodyCell rightEdge>
                                  <Stack direction="row" gap={1} alignItems="center">
                                    {isConform ? <IconCheck /> : <IconClose />}
                                    <span>{isConform ? "Conforme" : "Non conforme"}</span>
                                  </Stack>
                                </StyledBodyCell>
                              </TableRow>
                            )
                          })}

                          <StyledFooterCell colSpan={4}>
                            <Stack
                              width="264px"
                              direction="column"
                              alignItems="flex-start"
                              borderBottom="1px solid"
                              borderColor={COLORS.BLACK_500}
                              pb="6px"
                              gap="2px"
                            >
                              <Typography sx={sx.madeBy}>Fait par</Typography>
                              <Typography sx={sx.author}>
                                {formatEmailToName(test.author) || test.author}
                              </Typography>
                            </Stack>
                          </StyledFooterCell>
                        </TableBody>
                      </StyledTable>
                    </Box>
                  ) : null
                )}
            </Box>
          )
        })}
      </Box>
    </>
  )
}

const sx = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: "16px",
    border: "1px solid" + COLORS.PRIMARY_BORDER,
    borderRadius: "8px",
    padding: "16px",
  },
  thresholdBlock: {
    display: "flex",
    flexDirection: "column",
  },
  thresholdTitle: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
  },
  testBlock: {
    display: "flex",
    flexDirection: "column",
    marginTop: "16px",
  },
  header: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingLeft: "4px",
  },
  madeBy: {
    lineHeight: "22px",
    fontSize: "14px",
    color: COLORS.BLACK_500,
    margin: 0
  },
  author: {
    lineHeight: "22px",
    fontSize: "14px",
  },
  expandIcon : {
    cursor: "pointer",
    color: COLORS.BLACK_500,
    width: "24px",
    height: "24px"
  }
}

export default PEGazTest